import { useState } from 'react';
import { getCommodity } from '../utils/commodities';

/**
 * Professional crypto logo. Uses the community-maintained
 * `cryptocurrency-icons` package served from jsDelivr (color SVG variants).
 *
 * If the CDN request fails (unknown symbol / network hiccup) it falls back
 * to a subtle purple-gradient badge with the ticker inside — matching the
 * app aesthetic without looking amateurish.
 */
const CDN = 'https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color';

const CryptoIcon = ({ symbol, size = 40, className = '', ringColor = 'rgba(139,92,246,0.25)' }) => {
  const [failed, setFailed] = useState(false);
  const sym = (symbol || '').toLowerCase();

  const wrapperStyle = {
    width: size,
    height: size,
    minWidth: size,
    minHeight: size,
    boxShadow: `0 0 0 1px ${ringColor}, 0 4px 12px rgba(0,0,0,0.25)`,
  };

  const metal = getCommodity(sym);
  if (metal) {
    return (
      <img
        src={metal.icon}
        alt={`${metal.name} icon`}
        width={size}
        height={size}
        style={{ ...wrapperStyle, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}
        className={`rounded-full block ${className}`}
        data-testid={`crypto-icon-${symbol}`}
      />
    );
  }

  if (!sym || failed) {
    return (
      <div
        aria-label={symbol}
        style={wrapperStyle}
        className={`rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center font-bold text-white ${className}`}
        data-testid={`crypto-icon-fallback-${symbol}`}
      >
        <span style={{ fontSize: size * 0.32 }}>{(symbol || '?').slice(0, 4).toUpperCase()}</span>
      </div>
    );
  }

  return (
    <div
      style={wrapperStyle}
      className={`rounded-full bg-white/95 overflow-hidden flex items-center justify-center ${className}`}
      data-testid={`crypto-icon-${symbol}`}
    >
      <img
        src={`${CDN}/${sym}.svg`}
        alt={`${symbol} logo`}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setFailed(true)}
        style={{ width: size, height: size, display: 'block' }}
      />
    </div>
  );
};

export default CryptoIcon;
